'use client'

import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { arrayMove } from '@dnd-kit/sortable'
import { toast } from '@/hooks/use-toast'
import { useApiFetch } from '@/features/auth'
import {
  addCaseGoalFund,
  createCaseGoal,
  createCaseHolding,
  createCasePrescriptionStep,
  createCaseTrade,
  deleteCaseGoal,
  deleteCaseGoalFund,
  deleteCaseHolding,
  deleteCasePrescriptionStep,
  deleteCaseTrade,
  dialCase,
  replaceCaseHoldings,
  replaceCaseTrades,
  setCaseGoalPlanApproved,
  setMyCasePrescriptionStepDone,
  updateCallNotes,
  updateCallOutcome,
  updateCaseGoal,
  updateCaseGoalFund,
  updateCaseHolding,
  updateCasePrescriptionStep,
  updateCaseTrade,
  upsertCaseBuildProfile,
} from '@/features/cases/api/cases.api'
import type {
  CallSummary,
  CreateCaseGoalFundInput,
  CreateCaseGoalInput,
  CreateCaseHoldingInput,
  CreateCasePrescriptionStepInput,
  CreateCaseTradeInput,
  CasePrescriptionStep,
  DialCallResult,
  UpdateCallOutcomeInput,
  UpdateCaseGoalFundInput,
  UpdateCaseGoalInput,
  UpdateCaseHoldingInput,
  UpdateCaseTradeInput,
  UpsertCaseBuildProfileInput,
} from '@/features/cases/types'
import { AppError } from '@/lib/errors/appError'
import { queryKeys } from '@/lib/queryKeys'

function toastError(error: unknown) {
  toast({ variant: 'destructive', title: AppError.fromUnknown(error).message })
}

/** Trades' list key embeds pagination (`queryKeys.cases.trades(id, page, pageSize)`), so there's
 * no single exact key for "the open trades list regardless of page" — mirrors
 * `use-case-writes.ts`'s `invalidateCasesLists` predicate pattern. */
function invalidateCaseTrades(queryClient: QueryClient, caseId: string) {
  void queryClient.invalidateQueries({
    predicate: (q) =>
      q.queryKey[0] === 'cases' &&
      q.queryKey[1] === 'real' &&
      q.queryKey[2] === caseId &&
      q.queryKey[3] === 'trades',
  })
}

export function useAddCaseHolding(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateCaseHoldingInput) => createCaseHolding(authedFetch, caseId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.holdings(caseId) })
      // A new holding can flip `publishBlockers`' "needs at least one holding or trade" check,
      // which reads `counts.holdings`/`counts.trades` off the case-detail cache.
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.real(caseId) })
      toast({ variant: 'success', title: 'Holding added' })
    },
    onError: toastError,
  })
}

export function useRemoveCaseHolding(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (holdingId: string) => deleteCaseHolding(authedFetch, caseId, holdingId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.holdings(caseId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.real(caseId) })
      toast({ variant: 'success', title: 'Holding removed' })
    },
    onError: toastError,
  })
}

export function useUpdateCaseHolding(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ holdingId, input }: { holdingId: string; input: UpdateCaseHoldingInput }) =>
      updateCaseHolding(authedFetch, caseId, holdingId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.holdings(caseId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.real(caseId) })
      toast({ variant: 'success', title: 'Holding updated' })
    },
    onError: toastError,
  })
}

/** Replace-all — the whole holdings sheet is retyped/re-pasted, not diffed row by row. */
export function useReplaceCaseHoldings(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (inputs: CreateCaseHoldingInput[]) =>
      replaceCaseHoldings(authedFetch, caseId, inputs),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.holdings(caseId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.real(caseId) })
      toast({ variant: 'success', title: 'Holdings updated' })
    },
    onError: toastError,
  })
}

export function useAddCaseGoal(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateCaseGoalInput) => createCaseGoal(authedFetch, caseId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.goals(caseId) })
      // A new goal flips the "Total Goals" metric, which reads off the case-detail cache's counts.
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.real(caseId) })
      toast({ variant: 'success', title: 'Goal added' })
    },
    onError: toastError,
  })
}

export function useUpdateCaseGoal(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ goalId, input }: { goalId: string; input: UpdateCaseGoalInput }) =>
      updateCaseGoal(authedFetch, caseId, goalId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.goals(caseId) })
      toast({ variant: 'success', title: 'Goal updated' })
    },
    onError: toastError,
  })
}

/** Dedicated mutation for the `.../approve` route — toggling a plan's approval doesn't go through
 * `useUpdateCaseGoal`, which hits the general PATCH endpoint instead. */
export function useSetCaseGoalPlanApproved(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ goalId, approved }: { goalId: string; approved: boolean }) =>
      setCaseGoalPlanApproved(authedFetch, caseId, goalId, approved),
    onSuccess: (_data, { approved }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.goals(caseId) })
      toast({ variant: 'success', title: approved ? 'Plan approved' : 'Plan unapproved' })
    },
    onError: toastError,
  })
}

export function useRemoveCaseGoal(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (goalId: string) => deleteCaseGoal(authedFetch, caseId, goalId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.goals(caseId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.real(caseId) })
      toast({ variant: 'success', title: 'Goal removed' })
    },
    onError: toastError,
  })
}

export function useAddGoalFund(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ goalId, input }: { goalId: string; input: CreateCaseGoalFundInput }) =>
      addCaseGoalFund(authedFetch, caseId, goalId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.goals(caseId) })
      toast({ variant: 'success', title: 'Fund added' })
    },
    onError: toastError,
  })
}

export function useUpdateGoalFund(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      goalId,
      fundId,
      input,
    }: {
      goalId: string
      fundId: string
      input: UpdateCaseGoalFundInput
    }) => updateCaseGoalFund(authedFetch, caseId, goalId, fundId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.goals(caseId) })
      toast({ variant: 'success', title: 'Fund updated' })
    },
    onError: toastError,
  })
}

export function useRemoveGoalFund(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ goalId, fundId }: { goalId: string; fundId: string }) =>
      deleteCaseGoalFund(authedFetch, caseId, goalId, fundId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.goals(caseId) })
      toast({ variant: 'success', title: 'Fund removed' })
    },
    onError: toastError,
  })
}

/** Create-or-update — an Add-Lead-created Build Portfolio case has no profile row yet, so this
 * mutation covers both "add the first one" and "edit an existing one." Invalidates only
 * `cases.real(caseId)`: the profile is embedded on `CaseDetail.buildProfile`, not its own list. */
export function useUpsertCaseBuildProfile(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpsertCaseBuildProfileInput) =>
      upsertCaseBuildProfile(authedFetch, caseId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.real(caseId) })
      toast({ variant: 'success', title: 'Financial profile saved' })
    },
    onError: toastError,
  })
}

export function useAddCaseTrade(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateCaseTradeInput) => createCaseTrade(authedFetch, caseId, input),
    onSuccess: () => {
      invalidateCaseTrades(queryClient, caseId)
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.real(caseId) })
      toast({ variant: 'success', title: 'Trade added' })
    },
    onError: toastError,
  })
}

export function useRemoveCaseTrade(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (tradeId: string) => deleteCaseTrade(authedFetch, caseId, tradeId),
    onSuccess: () => {
      invalidateCaseTrades(queryClient, caseId)
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.real(caseId) })
      toast({ variant: 'success', title: 'Trade removed' })
    },
    onError: toastError,
  })
}

export function useUpdateCaseTrade(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ tradeId, input }: { tradeId: string; input: UpdateCaseTradeInput }) =>
      updateCaseTrade(authedFetch, caseId, tradeId, input),
    onSuccess: () => {
      invalidateCaseTrades(queryClient, caseId)
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.real(caseId) })
      toast({ variant: 'success', title: 'Trade updated' })
    },
    onError: toastError,
  })
}

/** Replace-all — the whole tradebook is retyped/re-pasted, not diffed row by row. */
export function useReplaceCaseTrades(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (inputs: CreateCaseTradeInput[]) => replaceCaseTrades(authedFetch, caseId, inputs),
    onSuccess: () => {
      invalidateCaseTrades(queryClient, caseId)
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.real(caseId) })
      toast({ variant: 'success', title: 'Trades updated' })
    },
    onError: toastError,
  })
}

export function useAddCasePrescriptionStep(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateCasePrescriptionStepInput) =>
      createCasePrescriptionStep(authedFetch, caseId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.prescriptionSteps(caseId) })
      // Flips `publishBlockers`' "needs at least one prescription step" check.
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.real(caseId) })
      toast({ variant: 'success', title: 'Step added' })
    },
    onError: toastError,
  })
}

export function useRemoveCasePrescriptionStep(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (stepId: string) => deleteCasePrescriptionStep(authedFetch, caseId, stepId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.prescriptionSteps(caseId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.real(caseId) })
      toast({ variant: 'success', title: 'Step removed' })
    },
    onError: toastError,
  })
}

export function useEditCasePrescriptionStep(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ stepId, input }: { stepId: string; input: CreateCasePrescriptionStepInput }) =>
      updateCasePrescriptionStep(authedFetch, caseId, stepId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.prescriptionSteps(caseId) })
      toast({ variant: 'success', title: 'Step updated' })
    },
    onError: toastError,
  })
}

/** Optimistic — a drag-and-drop reorder needs to feel instant, not wait out a network round trip.
 * `onMutate` reorders the cached list immediately via the same `arrayMove` dnd-kit itself uses (so
 * the optimistic result matches exactly what the drop animation already showed); `onError` rolls
 * back; `onSettled` reconciles with the server's real order. No toast — the reorder is its own
 * visible feedback. */
export function useReorderCasePrescriptionStep(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  const key = queryKeys.cases.prescriptionSteps(caseId)
  return useMutation({
    mutationFn: ({ stepId, position }: { stepId: string; position: number }) =>
      updateCasePrescriptionStep(authedFetch, caseId, stepId, { position }),
    onMutate: async ({ stepId, position }) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<CasePrescriptionStep[]>(key)
      queryClient.setQueryData<CasePrescriptionStep[]>(key, (steps) => {
        if (!steps) return steps
        const oldIndex = steps.findIndex((s) => s.id === stepId)
        const newIndex = Math.max(0, Math.min(steps.length - 1, position - 1))
        if (oldIndex === -1 || oldIndex === newIndex) return steps
        return arrayMove(steps, oldIndex, newIndex)
      })
      return { previous }
    },
    onError: (error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
      toastError(error)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: key })
    },
  })
}

/** Optimistic — the one case `frontend-code-standards.md` §7 calls for: cheap, instantly
 * reversible. `onMutate` flips the step's `done` in the cache immediately; `onError` rolls back;
 * `onSettled` reconciles with the server. No toast, same reasoning as reorder. Staff's own
 * done-toggle — hits the general staff PATCH endpoint (backed by a confirm dialog at the call
 * site, `case-prescription.tsx`, since staff acts on the investor's behalf). Also invalidates
 * `cases.real(caseId)`: a done-toggle now awards/reverses the step's `impactPoints` against the
 * case's `wealthScore`, which lives on the case-detail object, not the steps list. */
export function useToggleCasePrescriptionStepDone(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  const key = queryKeys.cases.prescriptionSteps(caseId)
  return useMutation({
    mutationFn: ({ stepId, done }: { stepId: string; done: boolean }) =>
      updateCasePrescriptionStep(authedFetch, caseId, stepId, { done }),
    onMutate: async ({ stepId, done }) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<CasePrescriptionStep[]>(key)
      queryClient.setQueryData<CasePrescriptionStep[]>(key, (steps) =>
        steps?.map((s) => (s.id === stepId ? { ...s, done } : s)),
      )
      return { previous }
    },
    onError: (error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
      toastError(error)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: key })
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.real(caseId) })
    },
  })
}

/** Investor's own done-toggle — hits the separate, investor-only `.../done` endpoint (never the
 * general staff PATCH), so it can never carry a `position`/text/priority/points edit. Same
 * optimistic shape as `useToggleCasePrescriptionStepDone`, immediate (no confirm dialog — this is
 * the investor's own action on their own step), and also invalidates `cases.real(caseId)` for the
 * same wealth-score reason. */
export function useSetMyPrescriptionStepDone(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  const key = queryKeys.cases.prescriptionSteps(caseId)
  return useMutation({
    mutationFn: ({ stepId, done }: { stepId: string; done: boolean }) =>
      setMyCasePrescriptionStepDone(authedFetch, caseId, stepId, done),
    onMutate: async ({ stepId, done }) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<CasePrescriptionStep[]>(key)
      queryClient.setQueryData<CasePrescriptionStep[]>(key, (steps) =>
        steps?.map((s) => (s.id === stepId ? { ...s, done } : s)),
      )
      return { previous }
    },
    onError: (error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
      toastError(error)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: key })
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.real(caseId) })
    },
  })
}

/** Predicate-based (not an exact key) — the calling `client-detail.tsx` scopes its own `useCalls`
 * query via `search: caseDetail.reference`, an object this helper has no direct access to;
 * matching the shared `['calls', 'real', 'list', ...]` prefix invalidates it (and the staff/admin
 * Calls list screens) without needing to know the exact params in use. */
function invalidateCallsLists(queryClient: QueryClient) {
  void queryClient.invalidateQueries({
    predicate: (q) =>
      q.queryKey[0] === 'calls' && q.queryKey[1] === 'real' && q.queryKey[2] === 'list',
  })
}

/** Click-to-call — fires the Plivo request and returns immediately with `status: 'initiating'`;
 * the caller polls `useCallStatus(result.id)` from there. No toast on success (the live-status
 * banner is the feedback); a failure still gets the shared error toast. */
export function useDialCall(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation<DialCallResult, unknown, void>({
    mutationFn: () => dialCase(authedFetch, caseId),
    onSuccess: () => invalidateCallsLists(queryClient),
    onError: toastError,
  })
}

/** The "wrap up this call" submission for a real Plivo call — updates the same `CallLog` row.
 * Also invalidates the case, since its `nextFollowUpAt` may have changed. */
export function useUpdateCallOutcome(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation<CallSummary, unknown, { callId: string; input: UpdateCallOutcomeInput }>({
    mutationFn: ({ callId, input }) => updateCallOutcome(authedFetch, callId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.real(caseId) })
      invalidateCallsLists(queryClient)
    },
    onError: toastError,
  })
}

/** Notes-only edit — applies to both a real Plivo call and a manually-logged one, from the
 * call-detail dialog. Same invalidation shape as `useUpdateCallOutcome`. */
export function useUpdateCallNotes(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation<CallSummary, unknown, { callId: string; notes: string }>({
    mutationFn: ({ callId, notes }) => updateCallNotes(authedFetch, callId, notes),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.real(caseId) })
      invalidateCallsLists(queryClient)
      toast({ variant: 'success', title: 'Notes saved' })
    },
    onError: toastError,
  })
}
